import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";

import {
  RemoveTenant,
  TenantsCard,
  WrapperContainer,
  MainWrapper
} from "./styles";
import {
  Button,
  TextPlain,
  TitleH1,
  TitleH2,
  TitleH3,
  TitleH4
} from "components/generic";
import HowItWork from "components/HowItWork";
import {
  ContainerValue,
  ContainerView,
  DialogWrapper,
  PullGrid,
  CorrectPullRefresh
} from "components/containers";
import { LoaderCustom } from "components/Loader";
import { theme } from "styles/theme";
import { HelpWidget } from "components/HelpWidget";
import {
  IMatch,
  IUserShort,
  ITenants,
  TTenantsHouse
} from "interfaces/intarfaces";
import { isoDate } from "utils/helpers";
import { DateLine } from "pages/ProfileUser/SimpleComponents";
import Modal from "components/Modal";
import {
  HOST_CHATS_ROUTE,
  HOST_CHATS_ROUTE_ID,
  MatchStatusEnum
} from "utils/constants";
import { UserAPI } from "API/userApi";
import { useContextHospi } from "context/ContextHospi";
import { useContextHost } from "context/ContextHost";
import { HostAPI } from "API/hostApi";
import { StudentAboutInfo } from "pages/ProfileUser/StudentAboutInfo";
import { useChat } from "../../context/ContextChat/ContextChat";

const getContractStatus = (
  start: string,
  end: string
): "future" | "current" | "past" => {
  if (!start.length || !end.length) return "future";
  const currentDate = isoDate(new Date());

  if (end < currentDate) return "past";
  if (start > currentDate) return "future";
  return "current";
};

interface IOffersData {
  student: string;
  house: string;
  offered_check_in_date: string | null;
  offered_check_out_date: string | null;
  status: "future" | "current" | "past";
  match_status: MatchStatusEnum;
}

export const MyTenants = () => {
  const { userProfile } = useContextHospi();
  const { housesShortData } = useContextHost();
  const { t, i18n } = useTranslation("host");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tenantsFuture, setTenantsFuture] = useState<ITenants[]>([]);
  const [tenantsCurrent, setTenantsCurrent] = useState<ITenants[]>([]);
  const [tenantsPast, setTenantsPast] = useState<ITenants[]>([]);
  const [currentStudent, setCurrentStudent] = useState<IUserShort | undefined>(
    undefined
  );
  const [showModal, setShowModal] = useState<number>(0);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchData = (isSubscribed: boolean) => {
    setLoading(true);
    HostAPI.getHostMatches(userProfile?.user_id!)
      .then((response) => {
        if (response.length) {
          let studentCache = new Map<string, IUserShort>();
          let houseCache = new Map<string, TTenantsHouse>();
          const arrayStudentID = new Set<string>();
          const arrayHouseID = new Set<string>();
          const arrayOffersData: IOffersData[] = [];
          response
            .filter(
              (item: IMatch) =>
                item.match_status && item.match_status > MatchStatusEnum.INVITED
            )
            .forEach((item: IMatch) => {
              arrayStudentID.add(item.student_id);
              arrayHouseID.add(item.house_id);
              arrayOffersData.push({
                student: item.student_id,
                house: item.house_id,
                offered_check_in_date: item.check_in_date ?? "",
                offered_check_out_date: item.check_out_date ?? "",
                status: getContractStatus(
                  item.check_in_date ?? "",
                  item.check_out_date ?? ""
                ),
                match_status: item.match_status!
              });
            });

          // Get data houses from Context
          if (!!housesShortData.length)
            housesShortData.forEach((item) => {
              if (arrayHouseID.has(item.house_id!))
                houseCache.set(item.house_id!, item as TTenantsHouse);
            });
          else throw new Error("Something went wrong, 106");

          Promise.allSettled(
            Array.from(arrayStudentID).map((item) =>
              UserAPI.getUserPreview(item)
            )
          ).then((results) => {
            results.forEach((item) => {
              item.status === "fulfilled" &&
                studentCache.set(
                  (item.value as IUserShort).user_id,
                  item.value as IUserShort
                );
            });
            const gotStudentData: string[] = Array.from(studentCache.keys());
            const futureOffers: ITenants[] = [];
            const currentOffers: ITenants[] = [];
            const pastOffers: ITenants[] = [];
            let tempTenat: ITenants;
            arrayOffersData.forEach((item) => {
              if (
                gotStudentData.includes(item.student) &&
                studentCache.get(item.student) !== undefined &&
                houseCache.get(item.house) !== undefined
              ) {
                tempTenat = {
                  student: studentCache.get(item.student)!,
                  house: houseCache.get(item.house)!,
                  contract: {
                    offered_check_in_date: item.offered_check_in_date ?? "",
                    offered_check_out_date: item.offered_check_out_date ?? "",
                    match_status: item.match_status
                  },
                  host_id: userProfile?.user_id!
                };
                item.status === "future" && futureOffers.push(tempTenat);
                item.status === "current" && currentOffers.push(tempTenat);
                item.status === "past" && pastOffers.push(tempTenat);
              }
            });
            if (isSubscribed) {
              setTenantsFuture(futureOffers);
              setTenantsCurrent(currentOffers);
              setTenantsPast(pastOffers);
              setLoading(false);
              setDataLoaded(true);
            }
          });
        } else if (isSubscribed) {
          setDataLoaded(true);
          setLoading(false);

        }
      })
      .catch(() => {
        if (isSubscribed) {
          setTenantsFuture([]);
          setTenantsCurrent([]);
          setTenantsPast([]);
          setLoading(false);
          setDataLoaded(true);
        }
      });
  };


  const removeTenant = (payload: IMatch) => {
    setLoading(true);
    HostAPI.removeTenant(payload).then((response) => {
      if (typeof response.status === "number" && response.status > 400) {
        setLoading(false);
        return;
      }
      if (response.hasOwnProperty("success") && response.success) {
        setTenantsCurrent((prev) =>
          prev.filter(
            (item) =>
              item.house.house_id !== payload.house_id &&
              item.student.user_id !== payload.student_id
          )
        );
        setTenantsFuture((prev) =>
          prev.filter(
            (item) =>
              item.house.house_id !== payload.house_id &&
              item.student.user_id !== payload.student_id
          )
        );
        setLoading(false);
      } else {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    let isSubscribed = true;
    if (housesShortData.length === 0 && dataLoaded) {
      isSubscribed && setLoading(false);
    } else {
      isSubscribed && setLoading(true);
      fetchData(isSubscribed);
    }
    // eslint-disable-next-line
  }, [housesShortData, dataLoaded]);


  useEffect(() => {
    currentStudent && setShowModal(1);
  }, [currentStudent]);

  useEffect(() => {
    !showModal && setCurrentStudent(undefined);
  }, [showModal]);

  const correctStyle = {
    gap: "1rem",
    paddingBottom: "1rem"
    // justifySelf: "end",
  };

  return (
    <ContainerView>

      <MainWrapper style={correctStyle}>
        {loading && (
          <LoaderCustom
            size={5}
            color={theme.colors.bgBody}
            text={t("tenants.loading")}
          />
        )}
        <CorrectPullRefresh>

          <PullToRefresh onRefresh={async () => {
            setDataLoaded(false);
            fetchData(true)
          }}>
            <PullGrid>
              <TitleH1>{t("tenants.title")}</TitleH1>
              <HowItWork
                title={t("tenants.howItWork.butHowItWork")}
                items={[t("tenants.howItWork.text")]}
              />

              {dataLoaded && !tenantsFuture.length &&
                !tenantsCurrent.length &&
                !tenantsPast.length && (
                  <Button
                    main
                    onClick={() =>
                      navigate(
                        generatePath(HOST_CHATS_ROUTE, {
                          lng: i18n.language
                        })
                      )
                    }
                  >
                    {t("tenants.goToMatches")}
                  </Button>
                )}

              {!!tenantsFuture.length && (
                <>
                  <TitleH2 style={{ justifySelf: "flex-start" }}>
                    {t("tenants.futureTenants")}
                  </TitleH2>
                  {tenantsFuture.map((item, num) => (
                    <TenantCard
                      {...item}
                      key={num}
                      showStudentInfo={setCurrentStudent}
                      deleteTenant={removeTenant}
                    />
                  ))}
                </>
              )}
              {!!tenantsCurrent.length && (
                <>
                  <TitleH2 style={{ justifySelf: "flex-start" }}>
                    {t("tenants.currentTenants")}
                  </TitleH2>
                  {tenantsCurrent.map((item, num) => (
                    <TenantCard
                      {...item}
                      key={num}
                      showStudentInfo={setCurrentStudent}
                      deleteTenant={removeTenant}
                    />
                  ))}
                </>
              )}
              {!!tenantsPast.length && (
                <>
                  <TitleH2 style={{ justifySelf: "flex-start" }}>
                    {t("tenants.pastTenants")}
                  </TitleH2>
                  {tenantsPast.map((item, num) => (
                    <TenantCard
                      {...item}
                      key={num}
                      showStudentInfo={setCurrentStudent}
                      deleteTenant={removeTenant}
                    />
                  ))}
                </>
              )}

              <HelpWidget
                title={t("tenants.help.title")}
                text={t("tenants.help.text").split(".")}
                btnText={t("tenants.help.buttonText")}
              />
            </PullGrid>
          </PullToRefresh>
        </CorrectPullRefresh>
      </MainWrapper>

      {!!showModal && currentStudent !== undefined && (
        <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
          <DialogWrapper>
            <StudentAboutInfo {...currentStudent} closeBtn={setShowModal} />
          </DialogWrapper>
        </Modal>
      )}
    </ContainerView>
  );
};

export const TenantCard: React.FC<
  ITenants & { showStudentInfo: Function; deleteTenant: Function }
> = ({ contract, student, house, host_id, showStudentInfo, deleteTenant }) => {
  const { t, i18n } = useTranslation("matches");
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<number>(0);
  const {
    state: { chats }
  } = useChat();

  const name = `${student.first_name} ${student.last_name}`;
  const address_room = `${house?.street ?? ""} ${house.house_number ?? ""} ${house.additional_house_number ?? ""
    }`;

  const isEdit =
    getContractStatus(
      contract.offered_check_in_date ?? "",
      contract.offered_check_out_date ?? ""
    ) !== "past" &&
    [
      MatchStatusEnum.ACCEPTED,
      MatchStatusEnum.PENDING,
      MatchStatusEnum.ACTIVE
    ].includes(contract.match_status!);

  const removeTenant = () => {
    deleteTenant({
      student_id: student.user_id,
      house_id: house.house_id!,
      host_id
    });
    setShowModal(0);
  };

  const houseChat = chats.find((c) => {
    return c.house_id === house.house_id;
  });

  return (
    <ContainerValue style={{ margin: 0 }}>
      <WrapperContainer>
        {isEdit ? (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <DateLine
              start={contract.offered_check_in_date ?? ""}
              end={contract.offered_check_out_date ?? ""}
              ico
              style={{
                marginBottom: "0.3em"
              }}
            />
            <RemoveTenant onClick={() => setShowModal(1)}>
              {t("host.card.buttons.removetenant")}
            </RemoveTenant>
          </div>
        ) : (
          <DateLine
            start={contract.offered_check_in_date ?? ""}
            end={contract.offered_check_out_date ?? ""}
            ico
            style={{
              color: theme.colors.textDimmed,
              alignSelf: "center",
              marginBottom: "0.3em"
            }}
          />
        )}
        <hr style={{ margin: "0 0 16px 0" }} />
        <TenantsCard>
          <img
            src={student.thumbnail_url + `?${new Date().toString()}`}
            alt="avatar"
          />
          <div>
            <TitleH3>{name}</TitleH3>
            <TitleH3>{address_room}</TitleH3>
            <TitleH4>{house.title}</TitleH4>
          </div>
          <div>
            <Button outline onClick={() => showStudentInfo(student)}>
              {t("host.card.studentInfo")}
            </Button>
            <Button
              normal
              onClick={() => {
                if (houseChat) {
                  navigate(
                    generatePath(HOST_CHATS_ROUTE_ID, {
                      lng: i18n.language,
                      id: houseChat.chat_id
                    })
                  );
                }
              }}
            >
              {t("host.card.buttons.chat")}
            </Button>
          </div>
        </TenantsCard>
      </WrapperContainer>
      {!!showModal && (
        <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
          {showModal === 1 && (
            <DialogWrapper>
              <TitleH2>{t(`host.removeMatch.title`)}</TitleH2>
              <TextPlain
                style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
              >
                {t(`host.removeMatch.text`, {
                  joinArrays: "\n"
                })}
              </TextPlain>
              <div className="buttons">
                <Button normal onClick={() => setShowModal(0)}>
                  {t(`host.removeMatch.buttonCancel`)}
                </Button>
                <Button danger onClick={removeTenant}>
                  {t(`host.removeMatch.buttonRemove`)}
                </Button>
              </div>
            </DialogWrapper>
          )}
        </Modal>
      )}
    </ContainerValue>
  );
};
